import type { Kit } from "@golazo/shared";
import { t } from "../i18n/index.js";
import type { PhoneMessage, Sender } from "../career/model.js";

/**
 * Del mensaje guardado (clave + parametros) al texto, en el idioma de ahora.
 * Un parametro que empieza por "§" es otra clave: el nombre de una division,
 * un estilo, una equipacion. Se traduce tambien.
 */
export function phoneText(msg: Pick<PhoneMessage, "key" | "params">): string {
  if (!msg.params) return t(msg.key);
  const params: Record<string, string | number> = {};
  // Las claves anidadas reciben los mismos parametros ("Tira {n} veces").
  const raw = Object.fromEntries(Object.entries(msg.params).filter(([, v]) => !(typeof v === "string" && v.startsWith("§"))));
  for (const [k, v] of Object.entries(msg.params)) {
    params[k] = typeof v === "string" && v.startsWith("§") ? (v.length > 1 ? t(v.slice(1), raw) : "") : v;
  }
  return t(msg.key, params).trim();
}

const SENDERS: Record<Sender, { icon: string; label: string }> = {
  coach: { icon: "📋", label: "Míster" },
  agent: { icon: "💼", label: "Agente" },
  press: { icon: "📰", label: "Prensa" },
  fans: { icon: "📣", label: "Afición" },
  mom: { icon: "❤️", label: "Mamá" },
  system: { icon: "🎽", label: "Fynta" },
  squad: { icon: "👥", label: "Vestuario" },
  rival: { icon: "😈", label: "Rival" },
};

export function senderOf(from: Sender): { icon: string; label: string } {
  const s = SENDERS[from];
  return { icon: s.icon, label: t(s.label) };
}

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** 1º, 2º… / 1st, 2nd… */
export function ordinal(n: number): string {
  return t("{n}º", { n });
}

function hex(n: number): string {
  return `#${n.toString(16).padStart(6, "0")}`;
}

/**
 * La ficha de un club o tuya: el octogono del campo con su equipacion y un
 * dorsal (o unas iniciales). Reutiliza los patrones de Personalizar
 * (`.kit-token--<patron>`), asi que una equipacion nueva no toca el CSS.
 */
export function kitTokenHtml(kit: Kit | undefined, label: string | number = "", size: "s" | "m" | "l" = "m"): string {
  if (!kit) return "";
  const style = `--kit-a:${hex(kit.primary)};--kit-b:${hex(kit.secondary)};--kit-c:${hex(kit.accent)};--kit-ink:${hex(kit.ink)}`;
  return `<span class="kit-token kit-token--${kit.pattern} ch-token ch-token--${size}" style="${style}" aria-hidden="true"><span class="kit-token-num">${escapeHtml(String(label))}</span></span>`;
}
